/**
 * NotificationsBell — Glocke + Ungelesen-Badge + lazy NotificationsPanel.
 * Redesign S2 (2026-10-10, `tasks/design-handoff-redesign-s2.md` Entscheid 2):
 * zurück in die Topbar (rechts, neben „Hochladen"); 2026-07-19 bis S2 lebte sie
 * im `SidebarAccount`. Eigene Komponente, damit useNotificationsPoll + Panel-
 * State gekapselt bleiben und Topbar-/Sidebar-Tests sie wegmocken können.
 *
 * Der Panel positioniert sich selbst am Trigger (`getBoundingClientRect`,
 * kippt bei Platzmangel) — oben rechts öffnet er nach unten, rechtsbündig.
 */
import { lazy, Suspense, useRef, useState } from 'react';
import { useNotificationsPoll } from '../hooks/useNotificationsPoll';
import { I } from '../lib/icons';

const NotificationsPanel = lazy(() =>
  import('./NotificationsPanel').then((m) => ({ default: m.NotificationsPanel })),
);

export function NotificationsBell() {
  const bellRef = useRef<HTMLButtonElement | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const { notifications, unreadCount } = useNotificationsPoll();

  return (
    <>
      <button
        ref={bellRef}
        type="button"
        aria-label={
          unreadCount > 0
            ? `Benachrichtigungen (${unreadCount} ungelesen)`
            : 'Benachrichtigungen'
        }
        aria-haspopup="dialog"
        aria-expanded={panelOpen}
        onClick={() => setPanelOpen((p) => !p)}
        className="relative flex items-center justify-center w-11 h-11 rounded-control text-ink-700 hover:bg-ink-100 hover:text-ink-900 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-500"
      >
        <I.bell size={20} />
        {unreadCount > 0 && (
          <span
            data-testid="notifications-badge"
            className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-brand-green-700 text-white text-2xs font-semibold flex items-center justify-center ring-2 ring-white"
            aria-hidden="true"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {panelOpen && (
        <Suspense fallback={null}>
          <NotificationsPanel
            open={panelOpen}
            onClose={() => {
              setPanelOpen(false);
              bellRef.current?.focus();
            }}
            bellRef={bellRef}
            notifications={notifications}
            unreadCount={unreadCount}
          />
        </Suspense>
      )}
    </>
  );
}
